import { MAGNET_VISUAL_PRESETS } from './magnetVisualDesign.js';
// Single source of truth for the magnet appearance/definition contract's
// enumerable vocab (display modes, theme modes, auto-open triggers, hub CTA
// action types) and the web-font allowlist. Previously three independently
// maintained copies that happened to agree — apps/service-api/src/routes/
// magnets.js's DISPLAY_MODES/THEME_MODES/TRIGGERS/ACTION_TYPES/ROUTE_KEYS,
// apps/client-saas/src/routes/magnets-route.js's DISPLAY_OPTS/THEME_OPTS/
// TRIGGER_OPTS/CTA_META, and shared/machfivemagnet.js's scattered inline
// literal checks in setupTrigger()/the CTA-type dispatch — the same drift
// shape stepTypes.js/leadCaptureFields.js closed off for step types and lead
// fields, deliberately deferred here until one of the three files was touched
// again anyway (see design/docs — this session's own passkey/GA4/templates
// work finally touched machfivemagnet.js repeatedly).
//
// shared/machfivemagnet.js is the one consumer that CANNOT import this
// module: it's a self-contained IIFE shipped raw as a <script> tag on
// arbitrary customer sites (only ever passed through terser, a minifier —
// no bundler, no module resolution at that point). Its hardcoded copies are
// cross-checked against this file by a permanent test instead
// (magnet-vocab-sdk-consistency.test.js in apps/client-saas), so a future
// drift there still fails loudly instead of silently — see that test's own
// header comment for exactly what it can and can't catch.

// Canonical order matches the client-saas configurator's presentation order
// (DISPLAY_OPTS/THEME_OPTS/TRIGGER_OPTS) where the two already agreed, so
// deriving the UI's option order directly from this array (same pattern
// magnet-flow.js already uses for STEP_TYPES/TYPE_ORDER) changes nothing
// about what a user sees.
export const DISPLAY_MODES = [
  { value: 'inline', label: 'Inline' },
  { value: 'chat', label: 'Chat' },
  { value: 'right_slide', label: 'Slide' },
  { value: 'modal', label: 'Modal' },
];
// Optional presentation; omitted values preserve every existing installation.
export const PRESENTATION_STYLES = [
  { value: 'conversation', label: 'Conversation' },
  { value: 'assistant', label: 'Assistant welcome' },
];
export const LAUNCHER_STYLES = [
  { value: 'label', label: 'Labeled button' },
  { value: 'button', label: 'Round button' },
  { value: 'invitation', label: 'Invitation bar' },
];
export const THEME_MODES = [
  // 'auto' follows the VISITOR'S OS via prefers-color-scheme. 'sync' follows the HOST
  // SITE's own theme — many sites let a visitor pick a theme independent of their OS,
  // and CSS inside a shadow root cannot see that choice, so the SDK reads <html>
  // directly (data-theme/data-mode/data-color-scheme, then color-scheme, then a
  // dark/theme-dark class) and mirrors it. A site using none of those conventions
  // degrades to the 'auto' behaviour, so 'sync' is never worse than 'auto'.
  { value: 'auto', label: 'Auto (visitor OS setting)' },
  { value: 'sync', label: "Sync (match the site's own dark mode)" },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];
// Auto-open triggers the SDK honors; 'none' (or any unknown value) means manual open only.
export const TRIGGERS = [
  { value: 'on_load', label: 'Immediately on page load' },
  { value: 'on_8_seconds', label: 'After 8 seconds' },
  { value: 'on_scroll_80_percent', label: 'At 80% scroll depth' },
  { value: 'on_exit_intent', label: 'On exit intent (mouse leaves the page)' },
  { value: 'none', label: 'Manual only (visitor clicks the launcher)' },
];
// Hub CTA types: call/email reveal a contact, book runs the in-panel scheduler
// (or a booking link), chat runs the guided flow, support runs a multi-select
// intake. Only call/email require a value (VALUE_REQUIRED_ACTION_TYPES).
// Order here is the backend's original validation-array order, NOT a UI
// presentation order — client-saas's own CTA_ORDER is a deliberately
// different curated display order (book/chat/call/email/support) and stays
// its own hand-authored constant; only the value SET is cross-checked.
export const ACTION_TYPES = [
  { value: 'book', label: 'Book' },
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'chat', label: 'Chat' },
  { value: 'support', label: 'Support' },
];
export const VALUE_REQUIRED_ACTION_TYPES = ['call', 'email'];
// Flow routes a step array can target: the default chat flow (mag_macro_steps) or a CTA route.
export const ROUTE_KEYS = ['chat', 'book', 'support'];

export const DISPLAY_MODE_VALUES = DISPLAY_MODES.map((m) => m.value);
export const THEME_MODE_VALUES = THEME_MODES.map((m) => m.value);
export const TRIGGER_VALUES = TRIGGERS.map((t) => t.value);

/**
 * 'manual' is an accepted INPUT alias for 'none', not a second canonical token.
 *
 * 'none' already means "Manual only (visitor clicks the launcher)". The device-aware
 * form reads far better as { desktop: 'on_load', mobile: 'manual' } than with 'none',
 * so the word is accepted and folded to the existing value. Adding it to TRIGGERS
 * instead would leave two tokens meaning one thing, and every consumer would have to
 * know both — the duplication this codebase keeps having to unpick.
 */
export const TRIGGER_ALIASES = Object.freeze({ manual: 'none' });

/** A single token → its canonical form, or null if it is not a trigger we honour. */
export function normalizeTriggerToken(value) {
  if (typeof value !== 'string') return null;
  const v = TRIGGER_ALIASES[value.trim()] || value.trim();
  return TRIGGER_VALUES.includes(v) ? v : null;
}

/**
 * Accepts EITHER the historical single string or the device-aware object, and returns
 * the canonical form — or null if nothing usable is in there.
 *
 *   'on_load'                                  -> 'on_load'
 *   { desktop: 'on_load', mobile: 'manual' }   -> { desktop: 'on_load', mobile: 'none' }
 *   { mobile: 'manual' }                       -> { desktop: 'none', mobile: 'none' }
 *
 * A missing side becomes 'none' rather than inheriting the other: a half-specified
 * object most likely means "only this device should auto-open", and defaulting the
 * unspecified side to auto-open would be the surprising reading of that.
 *
 * Shared so the API's validation and the SDK's runtime resolution cannot drift — the
 * SDK ships standalone and cannot import this, so its copy is pinned by a differential
 * test instead (see the SDK trigger tests).
 */
export function normalizeTrigger(value) {
  if (typeof value === 'string') return normalizeTriggerToken(value);
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const desktop = normalizeTriggerToken(value.desktop);
    const mobile = normalizeTriggerToken(value.mobile);
    if (desktop === null && mobile === null) return null;
    return { desktop: desktop || 'none', mobile: mobile || 'none' };
  }
  return null;
}
export const ACTION_TYPE_VALUES = ACTION_TYPES.map((a) => a.value);

// Web fonts the builder offers and the SDK's ensureWebFont() will actually load
// (via Google Fonts CSS2) — system/web-safe fonts aren't listed since they
// already render everywhere with no network fetch needed. `key` is the
// lowercased first-family match ensureWebFont() looks up by; `googleSpec` is
// the exact CSS2 family query string; `fallback` drives both the CSS stack's
// generic family and whether the picker labels it "(serif)".
export const WEB_FONT_ALLOWLIST = [
  { name: 'Inter', key: 'inter', googleSpec: 'Inter:wght@400;500;600;700', fallback: 'sans-serif' },
  { name: 'Poppins', key: 'poppins', googleSpec: 'Poppins:wght@400;500;600;700', fallback: 'sans-serif' },
  { name: 'Montserrat', key: 'montserrat', googleSpec: 'Montserrat:wght@400;500;600;700', fallback: 'sans-serif' },
  { name: 'Roboto', key: 'roboto', googleSpec: 'Roboto:wght@400;500;700', fallback: 'sans-serif' },
  { name: 'Open Sans', key: 'open sans', googleSpec: 'Open+Sans:wght@400;600;700', fallback: 'sans-serif' },
  { name: 'Lato', key: 'lato', googleSpec: 'Lato:wght@400;700', fallback: 'sans-serif' },
  { name: 'Nunito', key: 'nunito', googleSpec: 'Nunito:wght@400;600;700', fallback: 'sans-serif' },
  { name: 'Raleway', key: 'raleway', googleSpec: 'Raleway:wght@400;600;700', fallback: 'sans-serif' },
  { name: 'Work Sans', key: 'work sans', googleSpec: 'Work+Sans:wght@400;500;600;700', fallback: 'sans-serif' },
  { name: 'Oswald', key: 'oswald', googleSpec: 'Oswald:wght@400;500;600;700', fallback: 'sans-serif' },
  { name: 'Merriweather', key: 'merriweather', googleSpec: 'Merriweather:wght@400;700', fallback: 'serif' },
];

// Presets resolve to the existing font/theme_vars contract. They never change a
// customer's primary brand color by default; the gallery can explicitly apply
// a preset's brand color. No preset identifier is stored in the magnet API.
// Each fixed palette covers the transcript as well as the surrounding chrome.
export const MAGNET_THEME_COLOR_FIELDS = [
  { key: 'surface', id: 'surface', label: 'Card background' },
  { key: 'surface_2', id: 'surface2', label: 'Raised surface' },
  { key: 'msg_bg', id: 'messages', label: 'Conversation background' },
  { key: 'bot_bg', id: 'bot', label: 'Question background' },
  { key: 'border', id: 'border', label: 'Control borders' },
  { key: 'bot_border', id: 'botborder', label: 'Question borders' },
  { key: 'text', id: 'text', label: 'Main text' },
  { key: 'text_sub', id: 'textsub', label: 'Secondary text' },
  { key: 'meta', id: 'meta', label: 'Captions' },
];

export const MAGNET_APPEARANCE_PRESETS = [
  ...MAGNET_VISUAL_PRESETS,
  {
    id: 'clean', brand: '#0072ba', name: 'Clean', description: 'Quiet neutrals and clear system typography.',
    font: 'system-ui, sans-serif',
    light: { surface: '#ffffff', surface_2: '#f4f5f7', msg_bg: '#ffffff', bot_bg: '#f4f5f7', border: '#d6dbe2', bot_border: '#e5e8ed', text: '#182230', text_sub: '#526173', meta: '#607083', radius: '12px', radius_msg: '12px' },
    dark: { surface: '#1b222c', surface_2: '#252e3b', msg_bg: '#171d26', bot_bg: '#252e3b', border: '#465367', bot_border: '#354153', text: '#f2f4f7', text_sub: '#b8c3d1', meta: '#a8b6c8', radius: '12px', radius_msg: '12px' },
  },
  {
    id: 'warm', brand: '#a65c32', name: 'Warm', description: 'Soft earth tones and friendly rounded type.',
    font: 'Nunito, sans-serif',
    light: { surface: '#fffdf9', surface_2: '#f4efe7', msg_bg: '#fffdf9', bot_bg: '#f4efe7', border: '#d7cfc2', bot_border: '#e6ddd0', text: '#302a24', text_sub: '#695e52', meta: '#75695c', radius: '16px', radius_msg: '14px' },
    dark: { surface: '#292521', surface_2: '#38312b', msg_bg: '#211e1b', bot_bg: '#38312b', border: '#62564a', bot_border: '#4a4036', text: '#faf4eb', text_sub: '#d0c3b3', meta: '#beaf9c', radius: '16px', radius_msg: '14px' },
  },
  {
    id: 'editorial', brand: '#515c44', name: 'Editorial', description: 'Crisp edges and distinctive serif typography.',
    font: 'Georgia, serif',
    light: { surface: '#ffffff', surface_2: '#f2f2ef', msg_bg: '#ffffff', bot_bg: '#f2f2ef', border: '#cecec8', bot_border: '#e0e0d9', text: '#252622', text_sub: '#5f6059', meta: '#6d6e65', radius: '8px', radius_msg: '8px' },
    dark: { surface: '#252622', surface_2: '#34352f', msg_bg: '#1c1d19', bot_bg: '#34352f', border: '#595b50', bot_border: '#46483e', text: '#f5f5ed', text_sub: '#cacbbc', meta: '#b6b8a6', radius: '8px', radius_msg: '8px' },
  },
  {
    id: 'coast', name: 'Coast', description: 'Airy blue surfaces and straightforward type.', font: 'Arial, Helvetica, sans-serif', brand: '#1676a2',
    light: { surface: '#f6fbff', surface_2: '#e6f0f8', msg_bg: '#f6fbff', bot_bg: '#e6f0f8', border: '#bbc7cf', bot_border: '#bbc7cf', text: '#182230', text_sub: '#455062', meta: '#536077', radius: '14px', radius_msg: '12px' },
    dark: { surface: '#132534', surface_2: '#1d3548', msg_bg: '#132534', bot_bg: '#1d3548', border: '#596b7b', bot_border: '#596b7b', text: '#f4f7fb', text_sub: '#d1d9e2', meta: '#bbcbd8', radius: '14px', radius_msg: '12px' },
  },
  {
    id: 'forest', name: 'Forest', description: 'Calm greens with a grounded, natural feel.', font: 'system-ui, sans-serif', brand: '#35764a',
    light: { surface: '#f7fbf7', surface_2: '#e7f0e8', msg_bg: '#f7fbf7', bot_bg: '#e7f0e8', border: '#bbc7cf', bot_border: '#bbc7cf', text: '#182230', text_sub: '#455062', meta: '#536077', radius: '16px', radius_msg: '14px' },
    dark: { surface: '#17291f', surface_2: '#243b2c', msg_bg: '#17291f', bot_bg: '#243b2c', border: '#596b7b', bot_border: '#596b7b', text: '#f4f7fb', text_sub: '#d1d9e2', meta: '#bbcbd8', radius: '16px', radius_msg: '14px' },
  },
  {
    id: 'lavender', name: 'Lavender', description: 'Soft violet surfaces with a welcoming finish.', font: 'Lato, sans-serif', brand: '#7752b8',
    light: { surface: '#fcfaff', surface_2: '#eee9f8', msg_bg: '#fcfaff', bot_bg: '#eee9f8', border: '#bbc7cf', bot_border: '#bbc7cf', text: '#182230', text_sub: '#455062', meta: '#536077', radius: '20px', radius_msg: '18px' },
    dark: { surface: '#262033', surface_2: '#382d4b', msg_bg: '#262033', bot_bg: '#382d4b', border: '#596b7b', bot_border: '#596b7b', text: '#f4f7fb', text_sub: '#d1d9e2', meta: '#bbcbd8', radius: '20px', radius_msg: '18px' },
  },
  {
    id: 'rose', name: 'Rose', description: 'Muted blush and quietly expressive type.', font: 'Georgia, serif', brand: '#a33e69',
    light: { surface: '#fffafb', surface_2: '#f6e8ed', msg_bg: '#fffafb', bot_bg: '#f6e8ed', border: '#bbc7cf', bot_border: '#bbc7cf', text: '#182230', text_sub: '#455062', meta: '#536077', radius: '18px', radius_msg: '16px' },
    dark: { surface: '#302129', surface_2: '#44303a', msg_bg: '#302129', bot_bg: '#44303a', border: '#596b7b', bot_border: '#596b7b', text: '#f4f7fb', text_sub: '#d1d9e2', meta: '#bbcbd8', radius: '18px', radius_msg: '16px' },
  },
  {
    id: 'graphite', name: 'Graphite', description: 'Sharp neutrals and a confident, minimal frame.', font: 'Arial, Helvetica, sans-serif', brand: '#526174',
    light: { surface: '#fafafa', surface_2: '#eaecef', msg_bg: '#fafafa', bot_bg: '#eaecef', border: '#bbc7cf', bot_border: '#bbc7cf', text: '#182230', text_sub: '#455062', meta: '#536077', radius: '4px', radius_msg: '4px' },
    dark: { surface: '#1b1e23', surface_2: '#2b3038', msg_bg: '#1b1e23', bot_bg: '#2b3038', border: '#596b7b', bot_border: '#596b7b', text: '#f4f7fb', text_sub: '#d1d9e2', meta: '#bbcbd8', radius: '4px', radius_msg: '4px' },
  },
  {
    id: 'sunrise', name: 'Sunrise', description: 'Warm peach tones with a bright, friendly rhythm.', font: 'Lato, sans-serif', brand: '#b65422',
    light: { surface: '#fffaf5', surface_2: '#f9eadb', msg_bg: '#fffaf5', bot_bg: '#f9eadb', border: '#bbc7cf', bot_border: '#bbc7cf', text: '#182230', text_sub: '#455062', meta: '#536077', radius: '22px', radius_msg: '20px' },
    dark: { surface: '#30251e', surface_2: '#45352a', msg_bg: '#30251e', bot_bg: '#45352a', border: '#596b7b', bot_border: '#596b7b', text: '#f4f7fb', text_sub: '#d1d9e2', meta: '#bbcbd8', radius: '22px', radius_msg: '20px' },
  },
  {
    id: 'linen', name: 'Linen', description: 'Paper-like neutrals and relaxed serif type.', font: 'Georgia, serif', brand: '#7a663b',
    light: { surface: '#fcfbf5', surface_2: '#eeecdf', msg_bg: '#fcfbf5', bot_bg: '#eeecdf', border: '#bbc7cf', bot_border: '#bbc7cf', text: '#182230', text_sub: '#455062', meta: '#536077', radius: '10px', radius_msg: '8px' },
    dark: { surface: '#29291f', surface_2: '#3b3b2e', msg_bg: '#29291f', bot_bg: '#3b3b2e', border: '#596b7b', bot_border: '#596b7b', text: '#f4f7fb', text_sub: '#d1d9e2', meta: '#bbcbd8', radius: '10px', radius_msg: '8px' },
  },
  {
    id: 'studio', name: 'Studio', description: 'Precise monospace type and crisp geometry.', font: '"Courier New", monospace', brand: '#36658d',
    light: { surface: '#f8fafc', surface_2: '#e9eef4', msg_bg: '#f8fafc', bot_bg: '#e9eef4', border: '#bbc7cf', bot_border: '#bbc7cf', text: '#182230', text_sub: '#455062', meta: '#536077', radius: '6px', radius_msg: '4px' },
    dark: { surface: '#17212c', surface_2: '#273544', msg_bg: '#17212c', bot_bg: '#273544', border: '#596b7b', bot_border: '#596b7b', text: '#f4f7fb', text_sub: '#d1d9e2', meta: '#bbcbd8', radius: '6px', radius_msg: '4px' },
  },
  {
    id: 'teal', name: 'Teal', description: 'Fresh aquatic tones with a soft outline.', font: 'Verdana, Geneva, sans-serif', brand: '#187b73',
    light: { surface: '#f3fcfb', surface_2: '#e0f0ed', msg_bg: '#f3fcfb', bot_bg: '#e0f0ed', border: '#bbc7cf', bot_border: '#bbc7cf', text: '#182230', text_sub: '#455062', meta: '#536077', radius: '18px', radius_msg: '16px' },
    dark: { surface: '#142b2a', surface_2: '#223e3c', msg_bg: '#142b2a', bot_bg: '#223e3c', border: '#596b7b', bot_border: '#596b7b', text: '#f4f7fb', text_sub: '#d1d9e2', meta: '#bbcbd8', radius: '18px', radius_msg: '16px' },
  },
];

export function magnetAppearancePreset(id, mode = 'light') {
  const preset = MAGNET_APPEARANCE_PRESETS.find((p) => p.id === id);
  if (!preset) return null;
  const themeMode = mode === 'dark' ? 'dark' : 'light';
  return { ...(preset.visual_style ? { visual_style: preset.visual_style } : {}), font: preset.font, theme_mode: themeMode, theme_vars: { ...preset[themeMode] } };
}

// Match either palette independently of theme_mode: the user may just have
// switched its radio and needs the same style's opposite palette applied next.
export function matchMagnetAppearancePreset(font, themeVars, visualStyle) {
  const vars = themeVars && typeof themeVars === 'object' ? themeVars : {};
  return MAGNET_APPEARANCE_PRESETS.find((p) => (visualStyle === undefined || (p.visual_style || '') === visualStyle) && p.font === font && ['light', 'dark'].some((mode) => {
    const palette = p[mode];
    return Object.keys(vars).length === Object.keys(palette).length
      && Object.entries(palette).every(([key, value]) => String(vars[key]).toLowerCase() === value.toLowerCase());
  }))?.id || '';
}
